import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Card, Col, Form, Modal, Radio, Row, Select, Spin } from 'antd';
import { AntForm } from '@shared/styled/ant-form';
import { Formik, FormikProps } from 'formik';
import { useNavigate } from 'react-router-dom';
import { TextField, SelectField, CascaderField, DateField, RadioField, TextAreaField } from '@shared/modules/form';
import { DateFormat } from '@shared/constants';
import { useCashFlowTransactionForm, CashFlowTransactionFormValues, useCashRegisters, useCashRegisterOperationsWithSub } from '../hooks';

const CreateCashFlowTransactionForm: FC<FormikProps<CashFlowTransactionFormValues> & { id?: string }> = ({ handleSubmit, isSubmitting, values, setFieldValue, id }) => {
  const navigate = useNavigate();

  const { data: cashRegisters = [] } = useCashRegisters();

  const { data: operations = [] } = useCashRegisterOperationsWithSub();
  const operationOptions = useMemo(
    () => operations.map((op) => ({ value: op.id.toString(), label: op.name, children: op.children.map((c) => ({ value: c.id.toString(), label: c.name })) })),
    [operations],
  );

  const sourceRegister = useMemo(() => cashRegisters.find((r) => String(r.id) === values.cashRegisterId), [cashRegisters, values.cashRegisterId]);
  const targetRegister = useMemo(() => cashRegisters.find((r) => String(r.id) === values.target.cashRegisterId), [cashRegisters, values.target.cashRegisterId]);

  const cashRegisterOptions = useMemo(
    () =>
      cashRegisters.map((item) => {
        const isSelected = values.type === 'transfer' ? item.id.toString() === values.cashRegisterId || item.id.toString() === values.target.cashRegisterId : false;
        return (
          <Select.Option disabled={isSelected} key={item.id} value={item.id.toString()}>
            {item.name}
          </Select.Option>
        );
      }),
    [cashRegisters, values.cashRegisterId, values.target.cashRegisterId, values.type],
  );

  useEffect(() => {
    const isCreate = !id;
    const isTransfer = values.type === 'transfer';
    const isSameCurrency = sourceRegister?.currency.id === targetRegister?.currency.id;
    if (isCreate && isTransfer && isSameCurrency) {
      setFieldValue('target.amount', values.amount);
    }
  }, [sourceRegister?.currency.id, targetRegister?.currency.id, values.amount, id, values.type, setFieldValue]);

  const onCashRegisterIdSelect = useCallback(() => setFieldValue('amount', ''), [setFieldValue]);
  const onTargetCashRegisterIdSelect = useCallback(() => setFieldValue('target.amount', ''), [setFieldValue]);

  return (
    <Modal width={992} open={true} onCancel={() => navigate('/cash-flow/transactions')} confirmLoading={isSubmitting} onOk={() => handleSubmit()} title="Tranzaksiya əlavə et">
      <AntForm layout="vertical" component="div" size="large">
        <Row gutter={[12, 12]}>
          <Col xs={24}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
              <RadioField name="type">
                <Radio.Button disabled={!!id} value="income">
                  Mədaxil
                </Radio.Button>
                <Radio.Button disabled={!!id} value="expense">
                  Məxaric
                </Radio.Button>
                <Radio.Button disabled={!!id} value="transfer">
                  Transfer
                </Radio.Button>
              </RadioField>
            </div>
          </Col>

          {values.type === 'transfer' && (
            <Col xs={12}>
              <Card size="small" title="Kassadan" bodyStyle={{ paddingBottom: 0 }}>
                <Row gutter={12}>
                  <Col xs={24}>
                    <SelectField name="cashRegisterId" item={{ label: 'Kassa' }} input={{ placeholder: 'Kassa seçin...', onSelect: onCashRegisterIdSelect, disabled: !!id }}>
                      {cashRegisterOptions}
                    </SelectField>
                  </Col>
                  <Col xs={24} md={12}>
                    <TextField name="amount" item={{ label: 'Məbləğ' }} input={{ placeholder: 'Məbləği daxil edin...', disabled: !values.cashRegisterId || !!id }} />
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Valyuta">
                      <Select disabled={true} value={sourceRegister?.currency.code} placeholder="Valyuta seçin..." />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
          )}

          {values.type === 'transfer' && (
            <Col xs={12}>
              <Card size="small" title="Kassaya" bodyStyle={{ paddingBottom: 0 }}>
                <Row gutter={12}>
                  <Col xs={24}>
                    <SelectField name="target.cashRegisterId" item={{ label: 'Kassa' }} input={{ placeholder: 'Kassa seçin...', disabled: !!id }}>
                      {cashRegisterOptions}
                    </SelectField>
                  </Col>
                  <Col xs={24} md={12}>
                    <TextField
                      name="target.amount"
                      item={{ label: 'Məbləğ' }}
                      input={{
                        onSelect: onTargetCashRegisterIdSelect,
                        placeholder: 'Məbləği daxil edin...',
                        disabled: !values.target.cashRegisterId || sourceRegister?.currency.id === targetRegister?.currency.id || !!id,
                      }}
                    />
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Valyuta">
                      <Select placeholder="Valyuta seçin..." disabled={true} value={targetRegister?.currency.code} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
          )}

          {values.type !== 'transfer' && (
            <Col xs={12}>
              <Card size="small" title="Kassa" bodyStyle={{ paddingBottom: 0 }}>
                <Row gutter={12}>
                  <Col xs={24}>
                    <SelectField name="cashRegisterId" item={{ label: 'Kassa' }} input={{ onSelect: onCashRegisterIdSelect, placeholder: 'Kassa seçin...', disabled: !!id }}>
                      {cashRegisterOptions}
                    </SelectField>
                  </Col>
                  <Col xs={24} md={12}>
                    <TextField name="amount" item={{ label: 'Məbləğ' }} input={{ placeholder: 'Məbləği daxil edin...', disabled: !values.cashRegisterId || !!id }} />
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Valyuta">
                      <Select placeholder="Valyuta seçin..." disabled={true} value={sourceRegister?.currency.code} />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>
          )}

          <Col xs={24} md={values.type === 'transfer' ? 24 : 12}>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <CascaderField name="operationIds" input={{ options: operationOptions, placeholder: 'Kateqoriyanı seçin...' }} item={{ label: 'Məxaric kateqoriyası' }} />
              </Col>
              {values.type === 'transfer' && (
                <Col xs={24} md={12}>
                  <CascaderField name="incomeOperationIds" input={{ options: operationOptions, placeholder: 'Kateqoriyanı seçin...' }} item={{ label: 'Mədaxil kateqoriyası' }} />
                </Col>
              )}
              <Col xs={24} md={12}>
                <DateField name="operatedAt" input={{ placeholder: 'Tarix seçin...', format: DateFormat.DATE_TIME, showTime: true }} item={{ label: 'Əməliyyat tarixi' }} />
              </Col>
              <Col xs={24}>
                <TextAreaField name="description" item={{ label: 'Qeyd' }} input={{ placeholder: 'Qeydinizi daxil edin...', rows: 4 }} />
              </Col>
            </Row>
          </Col>
        </Row>
      </AntForm>
    </Modal>
  );
};

export const CreateCashFlowTransaction: FC = () => {
  const { initialValues, onSubmit, id, isLoading } = useCashFlowTransactionForm();

  if (isLoading) {
    return (
      <Modal open={true} footer={null} closable={false}>
        <Spin style={{ display: 'block', padding: '40px 0', textAlign: 'center' }} />
      </Modal>
    );
  }

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize={true}>
      {(f) => <CreateCashFlowTransactionForm {...f} id={id} />}
    </Formik>
  );
};
